import { getMessageImageContent, getTextFromMessage, isValidDataUrl } from "@/lib/messages";
import {
  ChatMessage,
  ChatMessageData,
  ImageChatContent,
  ToolCall,
  ThinkingContent,
} from "@/services/promptEditor.service";
import Tooltip from "@components/library/Tooltip";
import { Attachment } from "@components/PromptEditor/Chat/Message";
import { FormattedPrompt, formatPromptTemplateHTML } from "@components/PromptEditor/FormattedPrompt";
import { MessageRoleTag } from "@components/PromptEditor/MessageRoleTag";
import { PhotographIcon } from "@heroicons/react/outline";
import React, { useMemo, useState } from "react";
import { DescriptionDetails, DescriptionList, DescriptionTerm, TruncatedExpandable } from "./DescriptionList";
import { ImageModal } from "./ImageModal";
import { hlToast, ToastVariant } from "@components/library/Toast";
import { AutoFormattedText, LanguageSelectLocation } from "@components/library/FormattedText";
import { TemplateLanguage } from "@/types/app/prompt";
import ThinkingRenderer from "@components/PromptEditor/Chat/ThinkingRenderer";
import { ToolTag } from "@components/PromptEditor/VariableTags";
import JSONEditor from "./Code/JSONEditor";
import { TemplateToolOutput } from "@/types/app/log";

interface Props {
  messages: ChatMessage[];
  highlightVariables?: boolean;
  populateTemplate?: boolean;
  variables?: Record<string, any> | null;
  templateToolOutputs?: TemplateToolOutput[] | null;
  templateLanguage?: TemplateLanguage;
  className?: string;
  style?: React.CSSProperties;
  formatContent?: boolean;
  languageSelectLocation?: LanguageSelectLocation;
  /**
   * Default name to use for tool messages that don't have a name specified.
   * Resolution order:
   * 1. If the tool call ID matches a previous tool call, use that tool's name
   * 2. If no match found, use this toolName value
   * 3. If neither is available, the name remains unspecified
   */
  toolName?: string;
}

/** Render the Messages in the log drawers */
export const ChatMessages = ({
  messages,
  highlightVariables,
  populateTemplate,
  variables,
  templateToolOutputs,
  templateLanguage,
  className,
  style,
  formatContent = false,
  toolName,
  languageSelectLocation,
}: Props) => {
  // Messages with role: "tool" may not have "name" specified. (by design - users don't have to specify it as it's a response to a previous tool call specified by ID)
  // So we need to populate this manually.
  const toolCallIdToName = new Map<string, string>();
  for (const message of messages) {
    if (message.role === "assistant" && message.tool_calls) {
      for (const toolCall of message.tool_calls) {
        toolCallIdToName.set(toolCall.id, toolCall.function.name);
      }
    }
  }

  const messageKeys = useMemo(() => {
    let messageKeys = messages.map((message) => {
      const key =
        typeof message.content === "string"
          ? `${message.role}-${message.content?.slice(0, 100)}-${message.name}`
          : `${message.role}-${JSON.stringify(message.content)?.slice(0, 100)}-${message.name}`;
      return key;
    });
    // If any keys are the same, add a random suffix to the key.
    const uniqueKeys = new Set(messageKeys);
    if (uniqueKeys.size !== messageKeys.length) {
      messageKeys = messageKeys.map((key) => `${key}-${Math.random().toString(36).substring(2, 15)}`);
    }
    return messageKeys;
  }, [messages]);

  return (
    <DescriptionList className={className} style={style}>
      {messages.map((message, index) => {
        const key = messageKeys[index];
        let displayedMessage = message;
        if (message.role === "tool" && !message.name) {
          // Tool's output message doesn't have a tool name specified.
          // if we can find a tool_name from the list of all tool calls, use it.
          // else, if toolName is provided, use it.
          if (message.tool_call_id && toolCallIdToName.get(message.tool_call_id)) {
            displayedMessage = { ...message, name: toolCallIdToName.get(message.tool_call_id) };
          } else if (toolName) {
            displayedMessage = { ...message, name: toolName };
          }
        }
        return (
          <ChatMessageComponent
            // Need a unique key to avoid state (how its formatted) persisting across logs.
            // Messages can be very long, so cautious about using the entire message object as a key.
            // NOTE: some customers add metadata/ standard format to the message content;
            // I've bumped the slice for key from 40 to 100 to avoid collisions.
            key={key}
            {...displayedMessage}
            highlightVariables={highlightVariables}
            populateTemplate={populateTemplate}
            variables={variables}
            templateToolOutputs={templateToolOutputs}
            formatContent={formatContent}
            languageSelectLocation={languageSelectLocation}
            templateLanguage={templateLanguage}
            thinking={message.thinking}
          />
        );
      })}
    </DescriptionList>
  );
};

interface ChatMessageProps extends ChatMessageData {
  highlightVariables?: boolean;
  populateTemplate?: boolean;
  variables?: Record<string, any> | null;
  templateToolOutputs?: TemplateToolOutput[] | null;
  formatContent?: boolean;
  languageSelectLocation?: LanguageSelectLocation;
  templateLanguage?: TemplateLanguage;
  thinking?: ThinkingContent[] | null;
}

export const ChatMessageComponent = ({
  role,
  content,
  name,
  tool_calls,
  tool_call_id,
  highlightVariables,
  populateTemplate,
  variables,
  templateToolOutputs,
  formatContent,
  languageSelectLocation,
  templateLanguage,
  thinking,
}: ChatMessageProps) => {
  const messageText = useMemo(() => getTextFromMessage(content), [content]);
  const imageContent = getMessageImageContent(content);

  const renderedContent = populateTemplate ? (
    <FormattedPrompt
      prompt={messageText}
      inputs={variables}
      templateToolOutputs={templateToolOutputs}
      templateLanguage={templateLanguage}
    />
  ) : highlightVariables ? (
    formatPromptTemplateHTML(messageText)
  ) : !formatContent ? (
    messageText
  ) : (
    <AutoFormattedText text={messageText} languageSelectLocation={languageSelectLocation} />
  );

  const shouldShowThinking = role === "assistant" && thinking !== undefined && thinking !== null;

  return (
    <>
      <DescriptionTerm>
        <MessageRoleTag>{role}</MessageRoleTag>
      </DescriptionTerm>
      <DescriptionDetails className="whitespace-pre-wrap">
        {role === "tool" ? (
          <ToolResponseBody name={name || ""} content={messageText} id={tool_call_id || ""} />
        ) : (
          <>
            {name && <div className=" font-bold text-text-base-2">{name}</div>}

            <div className="flex flex-col gap-12 text-13-18">
              {tool_calls && tool_calls.length > 0
                ? tool_calls.map((tool_call, index) => <ToolCallAttachment key={index} tool_call={tool_call} />)
                : null}

              {shouldShowThinking && (
                <ThinkingRenderer thinking={thinking} languageSelectLocation={languageSelectLocation} />
              )}

              <div>{renderedContent}</div>
            </div>
            {imageContent.length > 0 && (
              <div className="flex flex-col gap-8">
                <div className="text-12-12 font-bold text-text-base-3">Image</div>
                {imageContent.map((image, i) => (
                  <ImageAttachment key={i} image={image} />
                ))}
              </div>
            )}
          </>
        )}
      </DescriptionDetails>
    </>
  );
};

const ToolCallId = ({ id }: { id: string }) => {
  return (
    <Tooltip content="Tool call ID" side="top">
      <button
        className="min-w-[24px] max-w-[92px] truncate text-10-10"
        onClick={async () => {
          // Copy ID to clipboard
          await navigator.clipboard.writeText(id);
          hlToast({
            variant: ToastVariant.Success,
            title: "ID copied to clipboard",
          });
        }}
      >
        {id}
      </button>
    </Tooltip>
  );
};

/** Tool Response - this the message body when the role="tool" */
const ToolResponseBody = ({ content, name, id }: { content: string; name: string; id: string }) => {
  return (
    <Attachment>
      <div className="flex min-w-128 flex-col font-mono text-12-16 text-text-base-3">
        <div className="flex items-center justify-between gap-12">
          <Tooltip content={name} side="top">
            {/* Need the div below to render ToolTip for some reason */}
            <div>
              <ToolTag>
                <span className="font-mono">{name}</span>
              </ToolTag>
            </div>
          </Tooltip>
          <ToolCallId id={id} />
        </div>
        <div className="mt-4">
          <JSONEditor
            defaultValue={content}
            showCopyButton={true}
            options={{
              lineNumbers: false,
              noIndentationGuides: false,
              lineDecorationsWidth: 0,
              autosize: true,
              scrollbar: {
                alwaysConsumeMouseWheel: false,
              },
            }}
            editorProps={{
              theme: "llama-white",
            }}
          />
        </div>
      </div>
    </Attachment>
  );
};

/** Tool call -- a tool call by an assistant in an attachment box*/
const ToolCallAttachment = ({ tool_call }: { tool_call: ToolCall }) => {
  return (
    <Attachment>
      <div className="flex min-w-128 flex-col font-mono text-12-16 text-text-base-3">
        <div className="flex items-center justify-between gap-12">
          <Tooltip content={tool_call.function.name} side="top">
            {/* Need the div below to render ToolTip for some reason */}
            <div>
              <ToolTag>
                <span className="font-mono">{tool_call.function.name}</span>
              </ToolTag>
            </div>
          </Tooltip>
          <ToolCallId id={tool_call.id} />
        </div>

        <div className="mt-4">
          <JSONEditor
            defaultValue={tool_call.function.arguments}
            showCopyButton={true}
            options={{
              lineNumbers: false,
              noIndentationGuides: false,
              lineDecorationsWidth: 0,
              autosize: true,
              scrollbar: {
                alwaysConsumeMouseWheel: false,
              },
            }}
            editorProps={{
              theme: "llama-white",
            }}
          />
        </div>
      </div>
    </Attachment>
  );
};

const ImageAttachment = ({ image }: { image: ImageChatContent }) => {
  const [imageError, setImageError] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  return (
    <div className="flex items-center justify-between gap-36">
      <div className="flex min-w-0 items-center gap-12">
        <div className="flex h-36 w-36 shrink-0 items-center justify-center rounded-ms">
          <img
            src={image.image_url.url}
            alt=""
            className="cursor-pointer rounded-ms object-contain"
            onError={() => setImageError(true)}
            onClick={() => setImageModalOpen(true)}
          />
          <ImageModal
            image={image.image_url.url}
            alt=""
            open={imageModalOpen}
            onClose={() => setImageModalOpen(false)}
          />
          {imageError && <PhotographIcon className="h-24 w-24 text-gray-400" />}
        </div>
        <div className="min-w-0 truncate font-mono text-12-16 text-text-base-1">{image.image_url.url}</div>
      </div>
    </div>
  );
};
